import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';

import { PowerPagesService } from '../../../core/services/power-pages.service';
import * as PowerPagesActions from './power-pages.actions';
import { selectPowerPagesMeta, selectPowerPagesLastQuery } from './power-pages.selectors';
import {
  DEFAULT_POWER_PAGES_CURRENT_PAGE,
  DEFAULT_POWER_PAGES_PER_PAGE,
} from '../models/power-page.model';

@Injectable()
export class PowerPagesEffects {
  private actions$ = inject(Actions);
  private powerPagesService = inject(PowerPagesService);
  private store = inject(Store);

  loadPowerPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerPagesActions.loadPowerPages),
      switchMap((action) =>
        this.powerPagesService
          .getPowerPagesList({
            page: action.page ?? DEFAULT_POWER_PAGES_CURRENT_PAGE,
            perPage: action.perPage ?? DEFAULT_POWER_PAGES_PER_PAGE,
            search: action.search ?? '',
            sort: action.sort,
            sortDirection: action.sortDirection,
          })
          .pipe(
            map((response) =>
              PowerPagesActions.loadPowerPagesSuccess({
                powerPages: response.data,
                meta: response.meta,
              })
            ),
            catchError((error) =>
              of(
                PowerPagesActions.loadPowerPagesFailure({
                  error: error?.message ?? 'Failed to load Power Pages',
                })
              )
            )
          )
      )
    )
  );

  deletePowerPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerPagesActions.deletePowerPage),
      switchMap((action) =>
        this.powerPagesService.deletePowerPage(action.uuid).pipe(
          map(() => PowerPagesActions.deletePowerPageSuccess()),
          catchError((error) =>
            of(
              PowerPagesActions.mutationFailure({
                error: error?.message ?? 'Failed to delete Power Page',
              })
            )
          )
        )
      )
    )
  );

  duplicatePowerPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerPagesActions.duplicatePowerPage),
      switchMap((action) =>
        this.powerPagesService.duplicatePowerPage(action.uuid).pipe(
          map(() => PowerPagesActions.duplicatePowerPageSuccess()),
          catchError((error) =>
            of(
              PowerPagesActions.mutationFailure({
                error: error?.message ?? 'Failed to duplicate Power Page',
              })
            )
          )
        )
      )
    )
  );

  reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PowerPagesActions.deletePowerPageSuccess,
        PowerPagesActions.duplicatePowerPageSuccess
      ),
      withLatestFrom(
        this.store.select(selectPowerPagesMeta),
        this.store.select(selectPowerPagesLastQuery)
      ),
      map(([, meta, lastQuery]) =>
        PowerPagesActions.loadPowerPages({
          page: meta?.currentPage ?? DEFAULT_POWER_PAGES_CURRENT_PAGE,
          perPage: meta?.perPage ?? DEFAULT_POWER_PAGES_PER_PAGE,
          search: lastQuery.search,
          sort: lastQuery.sort,
          sortDirection: lastQuery.sortDirection,
        })
      )
    )
  );

  loadPowerPageOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerPagesActions.loadPowerPageOptions),
      switchMap(() =>
        this.powerPagesService.getPowerPageOptions().pipe(
          map((options) => PowerPagesActions.loadPowerPageOptionsSuccess({ options })),
          catchError((error) =>
            of(
              PowerPagesActions.loadPowerPageOptionsFailure({
                error: error?.message ?? 'Failed to load Power Page options',
              })
            )
          )
        )
      )
    )
  );

  loadPowerPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerPagesActions.loadPowerPage),
      switchMap(({ uuid }) =>
        this.powerPagesService.getPowerPageById(uuid).pipe(
          map((powerPage) => PowerPagesActions.loadPowerPageSuccess({ powerPage })),
          catchError((error) =>
            of(
              PowerPagesActions.loadPowerPageFailure({
                error: error?.message ?? 'Failed to load Power Page',
              })
            )
          )
        )
      )
    )
  );

  savePowerPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerPagesActions.savePowerPage),
      switchMap(({ uuid, payload }) => {
        const request$ = uuid
          ? this.powerPagesService.updatePowerPage(uuid, payload)
          : this.powerPagesService.createPowerPage(payload);

        return request$.pipe(
          map((powerPage) => PowerPagesActions.savePowerPageSuccess({ powerPage })),
          catchError((error) =>
            of(
              PowerPagesActions.savePowerPageFailure({
                error: error?.message ?? 'Failed to save Power Page',
              })
            )
          )
        );
      })
    )
  );

  loadBlocks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerPagesActions.loadBlocks),
      switchMap(({ powerPageUuid }) =>
        this.powerPagesService.getBlocks(powerPageUuid).pipe(
          map((blocks) => PowerPagesActions.loadBlocksSuccess({ blocks })),
          catchError((error) =>
            of(PowerPagesActions.loadBlocksFailure({ error: error?.message ?? 'Failed to load blocks' }))
          )
        )
      )
    )
  );

  createBlock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerPagesActions.createBlock),
      switchMap(({ powerPageUuid, payload }) =>
        this.powerPagesService.createBlock(powerPageUuid, payload).pipe(
          map((block) => PowerPagesActions.saveBlockSuccess({ block, powerPageUuid })),
          catchError((error) =>
            of(PowerPagesActions.saveBlockFailure({ error: error?.message ?? 'Failed to create block' }))
          )
        )
      )
    )
  );

  editBlock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerPagesActions.editBlock),
      switchMap(({ blockUuid, powerPageUuid, payload }) =>
        this.powerPagesService.editBlock(blockUuid, payload).pipe(
          map((block) => PowerPagesActions.saveBlockSuccess({ block, powerPageUuid })),
          catchError((error) =>
            of(PowerPagesActions.saveBlockFailure({ error: error?.message ?? 'Failed to update block' }))
          )
        )
      )
    )
  );

  deleteBlock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerPagesActions.deleteBlock),
      switchMap(({ blockUuid }) =>
        this.powerPagesService.deleteBlock(blockUuid).pipe(
          map(() => PowerPagesActions.deleteBlockSuccess()),
          catchError((error) =>
            of(PowerPagesActions.deleteBlockFailure({ error: error?.message ?? 'Failed to delete block' }))
          )
        )
      )
    )
  );

  /** Reload the block list after any successful block mutation. */
  reloadBlocksAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PowerPagesActions.saveBlockSuccess),
      map(({ powerPageUuid }) => PowerPagesActions.loadBlocks({ powerPageUuid }))
    )
  );
}
